import isFunction from 'lodash/isFunction';
import EventEmitter from 'events';
import {
  MAX_VOLUME,
  MIN_VOLUME,
  setAdBannerVisibility,
  setLinearAdPlaying,
  setPlayerStatus,
  state,
} from '@/shared/components/player/store';
import { proxyArray } from '@/shared/utils/proxy';
import AdvertType from '@/shared/enums/advert-type';
import { PlayerStatus } from '@/shared/components/player/enums';
import { isFullscreen, toggleFullscreen } from '@/shared/utils';

const OVERLAY_TYPES = {
  520: {
    minPlayerWidth: 520,
    className: 'c-freewheel__ad-overlay-iframe--primary',
  },
  500: {
    minPlayerWidth: 1365,
    marginHeight: 30,
    marginWidth: 50,
    anchor: 'tl',
  },
  300: {
    minPlayerWidth: 900,
    maxPlayerWidth: 1365,
    marginHeight: 30,
    marginWidth: 50,
    anchor: 'tl',
  },
  200: {
    minPlayerWidth: 600,
    maxPlayerWidth: 900,
    marginHeight: 30,
    marginWidth: 50,
    anchor: 'tl',
  },
  150: {
    minPlayerWidth: 0,
    maxPlayerWidth: 600,
    marginHeight: 5,
    marginWidth: 10,
    anchor: 'tl',
  },
};

class FreewheelPlayer extends EventEmitter {
  constructor(opts) {
    super();

    const {
      getStatsCurrentTime,
      minBufferingTime,
      product,
      stats,
      video,
      assetDuration,
      assetId,
      breaks,
      customKeys,
      network,
      disabledAdvertTypes = [],
      profile,
      siteSection,
      url,
      $storage,
      $window,
    } = opts;

    this.$storage = $storage;
    this.$window = $window;
    this.SDK = window.tv.freewheel.SDK;
    this.playerWrapper = document.querySelector('.js-player-wrapper');
    this.freeWheelVideo = document.querySelector('video.js-freewheel-video');
    this.redGalaxyVideo = video;
    this.redGalaxyVideoFullscreen = false;
    this.$stats = stats;
    this.minBufferingTime = minBufferingTime;
    this.getStatsCurrentTime = getStatsCurrentTime;
    this.product = product;
    this.slots = {
      preroll: [],
      midroll: proxyArray({
        afterChangeHandler: (midroll) => {
          state.adsCuePoints = [...midroll].map(
            (advertisement) => ({ time: advertisement.getTimePosition() }),
          );
        },
      }),
      pause: [],
      postroll: [],
      overlay: [],
    };
    this.playMidrollTimeout = undefined;
    state.adsCuePoints = [];

    const params = {
      assetDuration,
      assetId,
      breaks,
      customKeys,
      network,
      disabledAdvertTypes,
      profile,
      siteSection,
      url,
    };

    this.params = {
      ...params,
    };

    this.adResponseLoaded = false;
    this.breaks = breaks || {};
    this.placement = this.breaks.placement || [];
    this.placement = !Array.isArray(this.placement) ? [this.placement] : this.placement;
    this.currentAdContext = null;
    this.lastVideoTime = assetDuration;
    this.isSeeking = false;
    setLinearAdPlaying(false);
  }

  onPlayerStatusChange(status) {
    if (status === PlayerStatus.ADVERTS_PLAYING) {
      // Additional check because something (FreeWheel?) mute this video in some cases despite of state.mute flag
      if (!state.mute && this.freeWheelVideo.muted) {
        this.unMute();
      }

      return this.play();
    }

    if (status === PlayerStatus.ADVERTS_PAUSE) {
      return this.pause();
    }

    if (state.isLinearAdPlaying) {
      return;
    }

    if (status !== PlayerStatus.PAUSE) {
      if (!this.slot) {
        return;
      }

      return this.slot.stop();
    }

    const pauseSlot = this.getCurrentPauseSlot();

    if (pauseSlot) {
      this.slot = pauseSlot;
      this.slot.play();

      const id = pauseSlot.getCustomId();
      const adIframe = document.querySelector(`[id*="_fw_ad_container_iframe_${id}"]`);
      adIframe?.classList.add('c-freewheel__ad-pause-iframe');
      setAdBannerVisibility(true);
    }
  }

  getCurrentPauseSlot() {
    const pauseSlots = this.slots.pause;
    const videoCurrentTime = this.redGalaxyVideo.currentTime;

    const { cuePoints = [] } = this.breaks;
    let since = 0;

    for (let i = 0; i < cuePoints.length; i++) {
      const { time: till } = cuePoints[i];

      if (videoCurrentTime >= since && videoCurrentTime < till) {
        return pauseSlots[i];
      }

      since = till;
    }

    return pauseSlots[0];
  }

  isAllowedAdvertType(type) {
    if (this.params.disabledAdvertTypes.includes(type)) {
      return false;
    }

    const allowedTypes = this.placement.map((placement) => placement.split('_')).flat();

    return allowedTypes.includes(AdvertType.ALL) || allowedTypes.includes(type);
  }

  addTemporalSlots() {
    const {
      ADUNIT_PREROLL,
      ADUNIT_MIDROLL,
      ADUNIT_OVERLAY,
      ADUNIT_PAUSE_MIDROLL,
      ADUNIT_POSTROLL,
    } = this.SDK;

    const { cuePoints = [] } = this.breaks;

    if (this.isAllowedAdvertType(AdvertType.PREROLL)) {
      this.currentAdContext.addTemporalSlot('preroll_1', ADUNIT_PREROLL, 0);
    }

    if (this.isAllowedAdvertType(AdvertType.MIDROLL)) {
      for (let i = 0; i < cuePoints.length; i++) {
        const { id, time } = cuePoints[i];
        this.currentAdContext.addTemporalSlot(`midroll_${id}`, ADUNIT_MIDROLL, time);
      }
    }

    this.currentAdContext.addTemporalSlot('pause_0', ADUNIT_PAUSE_MIDROLL, 0);
    for (let i = 0; i < cuePoints.length; i++) {
      const { time } = cuePoints[i];
      this.currentAdContext.addTemporalSlot(`pause_${i + 1}`, ADUNIT_PAUSE_MIDROLL, time + 1);
    }

    if (this.isAllowedAdvertType(AdvertType.OVERLAY)) {
      for (let i = 0; i < cuePoints.length; i++) {
        const { id, time } = cuePoints[i];
        this.currentAdContext.addTemporalSlot(`overlay_${id}`, ADUNIT_OVERLAY, time);
      }
    }

    if (this.isAllowedAdvertType(AdvertType.POSTROLL)) {
      this.currentAdContext.addTemporalSlot('postroll_1', ADUNIT_POSTROLL, this.params.assetDuration);
    }
  }

  attachListeners() {
    const {
      EVENT_AD,
      EVENT_REQUEST_COMPLETE,
      EVENT_SLOT_STARTED,
      EVENT_SLOT_ENDED,
    } = this.SDK;

    this.onAdTimeUpdateListener = (event) => this.onAdTimeUpdate(event);
    this.onLoadedMetadataListener = (event) => this.onLoadedMetadata(event);
    this.onContentVideoEndedListener = (event) => this.onContentVideoEnded(event);
    this.onContentTimeUpdatedListener = (event) => this.onContentTimeUpdated(event);
    this.onContentSeekStartListener = (event) => this.onContentSeekStart(event);
    this.onContentSeekEndListener = (event) => this.onContentSeekEnd(event);
    this.onAdEventListener = (event) => this.onAdEvent(event);
    this.onRequestCompleteListener = (event) => this.onRequestComplete(event);
    this.onSlotStartedListener = (event) => this.onSlotStarted(event);
    this.onSlotEndedListener = (event) => this.onSlotEnded(event);

    this.currentAdContext.addEventListener(EVENT_AD, this.onAdEventListener);
    this.currentAdContext.addEventListener(EVENT_REQUEST_COMPLETE, this.onRequestCompleteListener);
    this.currentAdContext.addEventListener(EVENT_SLOT_STARTED, this.onSlotStartedListener);
    this.currentAdContext.addEventListener(EVENT_SLOT_ENDED, this.onSlotEndedListener);
  }

  initAdManager() {
    const { network, url } = this.params;

    this.adManager = new this.SDK.AdManager();
    this.adManager.setNetwork(network);
    this.adManager.setServer(url);
  }

  initAdManagerContext() {
    const { assetId, assetDuration, customKeys, profile, siteSection } = this.params;
    const {
      PARAMETER_AUTO_PAUSE_AD_ONVISIBILITYCHANGE,
      PARAMETER_RENDERER_VIDEO_DISPLAY_CONTROLS_WHEN_PAUSE,
      PARAMETER_LEVEL_GLOBAL,
    } = this.SDK;

    this.currentAdContext = this.adManager.newContext();
    this.currentAdContext.setProfile(profile);
    this.currentAdContext.setVideoAsset(assetId, assetDuration);
    this.currentAdContext.setSiteSection(siteSection);

    this.currentAdContext.setParameter(PARAMETER_AUTO_PAUSE_AD_ONVISIBILITYCHANGE, false, PARAMETER_LEVEL_GLOBAL);
    this.currentAdContext.setParameter(PARAMETER_RENDERER_VIDEO_DISPLAY_CONTROLS_WHEN_PAUSE, false, PARAMETER_LEVEL_GLOBAL);

    customKeys.forEach(({ key, value }) => {
      this.currentAdContext.addKeyValue(key, value);
    });

    this.currentAdContext.registerVideoDisplayBase('freewheel');
  }

  init() {
    this.initAdManager();
    this.initAdManagerContext();
    this.addTemporalSlots();
    this.attachListeners();
    return new Promise((resolve) => {
      this.requestAdsPromiseResolve = resolve;
      this.currentAdContext.submitRequest();
    });
  }

  load() {
    if (this.isAllowedAdvertType(AdvertType.PREROLL) && this.slots.preroll.length) {
      this.playNextPreroll();
    } else {
      this.playContent();
    }
  }

  setVolume(volume) {
    this.freeWheelVideo.volume = volume / 100;
    this.currentAdContext.setAdVolume(volume / 100);
  }

  setMuted(muted) {
    this.freeWheelVideo.muted = muted;
  }

  mute() {
    this.setVolume(MIN_VOLUME);
    this.setMuted(true);
  }

  unMute() {
    this.setVolume(this.$storage.volume || MAX_VOLUME);
    this.setMuted(false);
  }

  pause() {
    this.freeWheelVideo.pause();
  }

  play() {
    this.freeWheelVideo.play();
  }

  playContent() {
    if (this.adResponseLoaded) {
      this.redGalaxyVideo.addEventListener('ended', this.onContentVideoEndedListener);
      this.redGalaxyVideo.addEventListener('timeupdate', this.onContentTimeUpdatedListener);
      this.redGalaxyVideo.addEventListener('seeking', this.onContentSeekStartListener);
      this.redGalaxyVideo.addEventListener('seeked', this.onContentSeekEndListener);

      if (this.currentAdContext) {
        this.currentAdContext.setVideoState(this.SDK.VIDEO_STATE_PLAYING);
      }
    }

    this.emit('playContent');
  }

  getCurrentSlotType() {
    if (!this.slot) {
      return '';
    }

    const {
      TIME_POSITION_CLASS_PREROLL,
      TIME_POSITION_CLASS_MIDROLL,
      TIME_POSITION_CLASS_POSTROLL,
    } = this.SDK;

    let type;

    switch (this.slot.getTimePositionClass()) {
      case TIME_POSITION_CLASS_PREROLL:
        type = AdvertType.PREROLL;
        break;
      case TIME_POSITION_CLASS_MIDROLL:
        type = AdvertType.MIDROLL;
        break;
      case TIME_POSITION_CLASS_POSTROLL:
        type = AdvertType.POSTROLL;
        break;
      default:
        return '';
    }

    return type.toLowerCase();
  }

  getCurrentAdIndex() {
    const adList = this.slot.getAdInstances();
    const currentAd = this.slot.getCurrentAdInstance();

    return adList.indexOf(currentAd) + 1;
  }

  playNextPreroll() {
    const { preroll } = this.slots;
    if (preroll.length) {
      this.slot = preroll.shift();
      this.slot.play();
    } else {
      setTimeout(() => this.playContent(), 100);
    }
  }

  playMidroll(i) {
    clearTimeout(this.playMidrollTimeout);

    if (this.slot) {
      this.slot.stop();
    }

    this.slot = this.slots.midroll[i];
    this.redGalaxyVideoFullscreen = isFullscreen(this.redGalaxyVideo);

    if (this.$window.isIOSDevice && this.redGalaxyVideoFullscreen) {
      toggleFullscreen(this.redGalaxyVideo, this.playerWrapper);
    }

    this.currentAdContext.setVideoState(this.SDK.VIDEO_STATE_PAUSED);
    this.playMidrollTimeout = setTimeout(() => {
      this.redGalaxyVideo.pause();
      this.slot.play();

      const customId = this.slot.getCustomId();
      const index = this.slots.midroll.findIndex((slot) => slot.getCustomId() === customId);

      if (index < 0) {
        return;
      }

      this.slots.midroll.splice(index, 1);
    }, 100);
  }

  playNextPostroll() {
    const {
      EVENT_REQUEST_COMPLETE,
      EVENT_SLOT_ENDED,
    } = this.SDK;

    const { postroll } = this.slots;
    if (this.isAllowedAdvertType(AdvertType.POSTROLL) && postroll.length > 0) {
      this.slot = postroll.shift();
      this.redGalaxyVideoFullscreen = isFullscreen(this.redGalaxyVideo);

      if (this.$window.isIOSDevice && this.redGalaxyVideoFullscreen) {
        toggleFullscreen(this.redGalaxyVideo, this.playerWrapper);
      }

      this.slot.play();
    } else {
      if (this.currentAdContext) {
        this.currentAdContext.removeEventListener(EVENT_REQUEST_COMPLETE, this.onRequestCompleteListener);
        this.currentAdContext.removeEventListener(EVENT_SLOT_ENDED, this.onSlotEndedListener);
      }

      this.currentAdContext = null;
      this.adManager = null;
      this.emit('ended');
    }
  }

  resumeContentAfterMidroll() {
    this.currentAdContext.setVideoState(this.SDK.VIDEO_STATE_PLAYING);
    this.emit('midrollEnded');

    if (this.$window.isIOSDevice && this.redGalaxyVideoFullscreen) {
      toggleFullscreen(this.redGalaxyVideo, this.playerWrapper);
    }

    this.redGalaxyVideo.play();
  }

  onRequestComplete({ success }) {
    if (success) {
      this.adResponseLoaded = true;

      const temporalSlots = this.currentAdContext.getTemporalSlots();
      const {
        TIME_POSITION_CLASS_PREROLL,
        TIME_POSITION_CLASS_MIDROLL,
        TIME_POSITION_CLASS_OVERLAY,
        TIME_POSITION_CLASS_PAUSE_MIDROLL,
        TIME_POSITION_CLASS_POSTROLL,
      } = this.SDK;
      const {
        preroll,
        midroll,
        overlay,
        pause,
        postroll,
      } = this.slots;

      for (let i = 0; i < temporalSlots.length; i++) {
        const slot = temporalSlots[i];

        switch (slot.getTimePositionClass()) {
          case TIME_POSITION_CLASS_PREROLL:
            preroll.push(slot);
            break;
          case TIME_POSITION_CLASS_MIDROLL:
            midroll.push(slot);
            break;
          case TIME_POSITION_CLASS_OVERLAY:
            overlay.push(slot);
            break;
          case TIME_POSITION_CLASS_PAUSE_MIDROLL:
            pause.push(slot);
            break;
          case TIME_POSITION_CLASS_POSTROLL:
            postroll.push(slot);
            break;
        }
      }
    }

    this.requestAdsPromiseResolve();
  }

  isLinearAdSlot(slot) {
    const {
      TIME_POSITION_CLASS_PREROLL,
      TIME_POSITION_CLASS_MIDROLL,
      TIME_POSITION_CLASS_POSTROLL,
    } = this.SDK;
    const timePositionClass = slot.getTimePositionClass();

    return [TIME_POSITION_CLASS_PREROLL, TIME_POSITION_CLASS_MIDROLL, TIME_POSITION_CLASS_POSTROLL].includes(timePositionClass);
  }

  isOverlayAdSlot(slot) {
    const { TIME_POSITION_CLASS_OVERLAY } = this.SDK;
    const timePositionClass = slot.getTimePositionClass();

    return TIME_POSITION_CLASS_OVERLAY === timePositionClass;
  }

  onAdEvent({ subType, slot, errorCode }) {
    this.emit(subType);

    const {
      EVENT_AD_BUFFERING_START,
      EVENT_AD_BUFFERING_END,
      EVENT_AD_COMPLETE,
      EVENT_AD_INITIATED,
      EVENT_ERROR,
    } = this.SDK;

    if (!this.isLinearAdSlot(slot)) {
      return;
    }

    const type = this.getCurrentSlotType();
    const position = this.getCurrentAdIndex();
    const breakTotalAds = slot.getAdCount();
    const {
      preroll,
      midroll,
      postroll,
    } = this.slots;
    const allPlayableAds = [...preroll, ...midroll, ...postroll];
    const totalCount = allPlayableAds.length;
    const index = this.breaks.cuePoints.findIndex(({ id }) => slot.getCustomId().includes(id));
    const breakPosition = index !== -1 ? index + 1 : undefined;
    const quality = `${this.slot.getWidth()}x${this.slot.getHeight()}`;
    const adInstance = this.slot.getCurrentAdInstance();
    const volume = Math.round(adInstance?._context?._adVolume * 100) || 0;
    const duration = adInstance?._creative?._duration || 0;
    const adId = adInstance?._creative?._id || 0;

    switch (subType) {
      case EVENT_AD_COMPLETE:
      case EVENT_ERROR:
        this.$stats.adFinish({ adId, duration, volume, quality, errorCode, type, position, breakPosition, breakTotalAds, totalCount }, this.product);
        break;
      case EVENT_AD_INITIATED:
        this.$stats.adStart({ adId, duration, volume, quality, type, position, breakPosition, breakTotalAds, totalCount }, this.product);
        break;
      case EVENT_AD_BUFFERING_START:
        this.bufferingStartTime = new Date().getTime();
        break;
      case EVENT_AD_BUFFERING_END:
        const bufferingDuration = (new Date().getTime() - this.bufferingStartTime) / 1000;
        const minBufferingTime = (this.minBufferingTime || 200) / 1000;
        if (bufferingDuration >= minBufferingTime) {
          this.$stats.bufferingWhilePlaying(this.product, this.getStatsCurrentTime(), Math.round(bufferingDuration), {
            type,
            position,
          });
        }
        this.bufferingStartTime = 0;
        break;
    }
  }

  onLoadedMetadata({ type, target }) {
    this.duration = target.duration;
    this.emit(type, {
      duration: target.duration,
    });
  }

  onAdTimeUpdate({ type, target }) {
    this.emit(type, {
      adIndex: this.getCurrentAdIndex(),
      currentTime: target.currentTime,
    });
  }

  onContentTimeUpdated() {
    const {
      midroll,
      overlay,
    } = this.slots;

    if (overlay.length === 0 && midroll.length === 0) {
      this.redGalaxyVideo.removeEventListener('timeupdate', this.onContentTimeUpdatedListener);
      return;
    }

    if (state.isLinearAdPlaying) {
      return;
    }

    const videoCurrentTime = this.redGalaxyVideo.currentTime;
    this.lastVideoTime = this.isSeeking ? this.lastVideoTime : videoCurrentTime;

    if (this.isAllowedAdvertType(AdvertType.OVERLAY)) {
      for (let i = 0; i < overlay.length; i++) {
        const slot = overlay[i];
        const slotTimePosition = slot.getTimePosition();

        if (Math.abs(videoCurrentTime - slotTimePosition) < 0.5) {
          this.slots.overlay.splice(i, 1);
          const overlay = this.getOverlay(slot);

          if (!overlay) {
            return;
          }

          this.setOverlayParameters(slot, overlay);

          this.slot = slot;
          this.slot.play();

          this.prepareOverlayPlacement(overlay);

          setPlayerStatus(PlayerStatus.PLAYING);
          return;
        }
      }
    }

    if (this.isAllowedAdvertType(AdvertType.MIDROLL)) {
      for (let i = 0; i < midroll.length; i++) {
        const slotTimePosition = midroll[i].getTimePosition();
        if (Math.abs(videoCurrentTime - slotTimePosition) < 0.5) {
          this.playMidroll(i);
          break;
        }
      }
    }
  }

  getSlotCreativeWidth(slot) {
    const slotAdInstances = slot.getAdInstances();
    const slotCreativeRenditions = slotAdInstances[0]?.getRenderableCreativeRenditions();

    return slotCreativeRenditions[0]?.getWidth();
  }

  getOverlay(slot) {
    const slotCreativeWidth = this.getSlotCreativeWidth(slot);
    const overlay = OVERLAY_TYPES[slotCreativeWidth];
    const playerWidth = this.playerWrapper.offsetWidth;

    if (overlay && playerWidth >= overlay.minPlayerWidth && (!overlay.maxPlayerWidth || playerWidth < overlay.maxPlayerWidth)) {
      return overlay;
    }
  }

  setOverlayParameters(slot, overlay) {
    const {
      PARAMETER_RENDERER_HTML_PRIMARY_ANCHOR,
      PARAMETER_RENDERER_HTML_MARGIN_HEIGHT,
      PARAMETER_RENDERER_HTML_MARGIN_WIDTH,
      PARAMETER_LEVEL_OVERRIDE,
    } = this.SDK;

    const { anchor, marginHeight, marginWidth } = overlay;

    slot.setParameter(PARAMETER_RENDERER_HTML_PRIMARY_ANCHOR, anchor, PARAMETER_LEVEL_OVERRIDE);
    slot.setParameter(PARAMETER_RENDERER_HTML_MARGIN_HEIGHT, marginHeight, PARAMETER_LEVEL_OVERRIDE);
    slot.setParameter(PARAMETER_RENDERER_HTML_MARGIN_WIDTH, marginWidth, PARAMETER_LEVEL_OVERRIDE);
  }

  addCloseButton(adContainer, style, onClick) {
    const closeButton = document.createElement('div');
    closeButton.classList.add('c-freewheel__close');
    closeButton.style.right = style.right;
    closeButton.style.top = style.top;
    closeButton.style.transform = style.transform;
    closeButton.addEventListener('click', () => isFunction(onClick) && onClick());

    const icon = document.createElement('i');
    icon.classList.add('o-icon', 'o-icon--close', 'o-typography--action');

    closeButton.appendChild(icon);
    adContainer.appendChild(closeButton);
  }

  prepareOverlayPlacement(overlay) {
    const slot = this.slot;
    const id = slot.getCustomId();
    const adContainer = document.querySelector(`[id*="_fw_ad_container_html_${id}"]`);
    const adIframe = document.querySelector(`[id*="_fw_ad_container_iframe_${id}"]`);

    adContainer?.classList.add('c-freewheel__ad-overlay-container');
    adIframe?.classList.add('c-freewheel__ad-overlay-iframe');

    if (overlay.className) {
      adIframe?.classList.add(overlay.className);
    }

    const computedStyle = getComputedStyle(adIframe, null);
    const isAdIframeDisplayed = adIframe && computedStyle.display !== 'none';

    if (isAdIframeDisplayed) {
      this.addCloseButton(adContainer, computedStyle, () => slot.stop());
    }
  }

  onContentSeekStart() {
    this.isSeeking = true;
  }

  onContentSeekEnd() {
    this.isSeeking = false;
    const {
      midroll,
      overlay,
    } = this.slots;

    if (overlay.length === 0 && midroll.length === 0) {
      this.redGalaxyVideo.removeEventListener('seeking', this.onContentSeekStartListener);
      this.redGalaxyVideo.removeEventListener('seeked', this.onContentSeekEndListener);
      return;
    }

    const videoCurrentTime = this.redGalaxyVideo.currentTime;
    const startTimePoint = this.lastVideoTime;
    const endTimePoint = videoCurrentTime;

    if (startTimePoint > endTimePoint) {
      return;
    }

    if (this.isAllowedAdvertType(AdvertType.MIDROLL)) {
      for (let i = midroll.length - 1; i >= 0; i--) {
        const slotTimePosition = midroll[i].getTimePosition();
        if (startTimePoint < slotTimePosition && slotTimePosition < endTimePoint) {
          this.playMidroll(i);
          break;
        }
      }
    }
  }

  onSlotStarted({ slot }) {
    const { EVENT_SLOT_STARTED } = this.SDK;
    const isLinearAdSlot = this.isLinearAdSlot(slot);
    const isOverlayAdSlot = this.isOverlayAdSlot(slot);

    if (isLinearAdSlot) {
      setLinearAdPlaying(true);
    }

    if (isLinearAdSlot || isOverlayAdSlot) {
      this.emit(EVENT_SLOT_STARTED, {
        adCount: slot.getAdCount(),
      });
      this.freeWheelVideo.addEventListener('timeupdate', this.onAdTimeUpdateListener);
      this.freeWheelVideo.addEventListener('loadedmetadata', this.onLoadedMetadataListener);
    }
  }

  onSlotEnded({ slot }) {
    setAdBannerVisibility(false);

    const {
      TIME_POSITION_CLASS_PREROLL,
      TIME_POSITION_CLASS_MIDROLL,
      TIME_POSITION_CLASS_POSTROLL,
      EVENT_SLOT_ENDED,
    } = this.SDK;
    const timePositionClass = slot.getTimePositionClass();

    setLinearAdPlaying(false);

    switch (timePositionClass) {
      case TIME_POSITION_CLASS_PREROLL:
        this.playNextPreroll();
        break;
      case TIME_POSITION_CLASS_MIDROLL:
        this.resumeContentAfterMidroll();
        break;
      case TIME_POSITION_CLASS_POSTROLL:
        this.playNextPostroll();
        break;
    }

    this.emit(EVENT_SLOT_ENDED);

    this.freeWheelVideo.removeEventListener('timeupdate', this.onAdTimeUpdateListener);
    this.freeWheelVideo.removeEventListener('loadedmetadata', this.onLoadedMetadataListener);
  }

  onContentVideoEnded() {
    this.freeWheelVideo.removeEventListener('ended', this.onContentVideoEnded);

    if (this.currentAdContext) {
      this.currentAdContext.setVideoState(this.SDK.VIDEO_STATE_STOPPED);
    }

    this.playNextPostroll();
  }

  destroy() {
    const {
      EVENT_AD,
      EVENT_REQUEST_COMPLETE,
      EVENT_SLOT_STARTED,
      EVENT_SLOT_ENDED,
    } = this.SDK;

    if (this.currentAdContext) {
      this.currentAdContext.removeEventListener(EVENT_AD, this.onAdEventListener);
      this.currentAdContext.removeEventListener(EVENT_REQUEST_COMPLETE, this.onRequestCompleteListener);
      this.currentAdContext.removeEventListener(EVENT_SLOT_STARTED, this.onSlotStartedListener);
      this.currentAdContext.removeEventListener(EVENT_SLOT_ENDED, this.onSlotEndedListener);
    }

    if (this.freeWheelVideo) {
      this.freeWheelVideo.removeEventListener('timeupdate', this.onAdTimeUpdateListener);
      this.freeWheelVideo.removeEventListener('loadedmetadata', this.onLoadedMetadataListener);
    }

    if (this.redGalaxyVideo) {
      this.redGalaxyVideo.removeEventListener('ended', this.onContentVideoEndedListener);
      this.redGalaxyVideo.removeEventListener('timeupdate', this.onContentTimeUpdatedListener);
      this.redGalaxyVideo.removeEventListener('seeking', this.onContentSeekStartListener);
      this.redGalaxyVideo.removeEventListener('seeked', this.onContentSeekEndListener);
    }

    this.removeAllListeners();
  }
}

export { FreewheelPlayer };
